import { match } from 'ts-pattern';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type {
  ReadonlyListItem,
  ReadonlyListItemOneOfRecord,
  ReadonlyListItemRecord,
} from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { TranslatedText } from '@interxfi/translation/components/translated-text';
import {
  type IxcOrdersV1BatchAllocationAvgPriceTransaction,
  type IxcOrdersV1BatchAllocationCustomerTransaction,
  type IxcOrdersV1BatchOrderControls,
  type IxcOrdersV1BatchOrderControlsSettings,
  type IxcOrdersV1BatchOrderControlsSettingsUsEquities,
  type IxcOrdersV1BatchRemainderInventoryTransaction,
  type IxcOrdersV1Commission,
  type IxcOrdersV1MiddleMoney,
  type IxcOrdersV1OrderAmount,
  type IxcOrdersV1OrderAmountConversion,
  type IxcOrdersV1OrderControls,
  type IxcOrdersV1OrderControlsSettings,
  type IxcOrdersV1OrderControlsSettingsUsEquities,
  type IxcOrdersV1OrderData,
  type IxcOrdersV1OrderExtension,
  type IxcOrdersV1OrderInstruction,
  type IxcOrdersV1OrderInstructionTaxLotSelection,
  type IxcOrdersV1OrderKeys,
  type IxcOrdersV1OrderState,
  IxcOrdersV1Side,
  type IxcOrdersV1TradeTransaction,
  type IxcOrdersV1TrfReportRef,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { getBooleanFromUndefinedOrNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import type { ReplaceValuesAndMakeOptional } from '@interxfi/utilities/types/replace';
import type { ViewContext, ViewContextType } from '../../util/view-context';
import { getAssetStateListItems } from '../asset/asset-list-items';
import { getAssetDetailPath } from '../asset/asset-routing';
import { getFixOrderDetailPath } from '../fix/order/fix-order-routing';
import { getInventoryBooksOrderDetailPath } from '../inventory/books-order/inventory-books-order-routing';
import { getInventoryFirmOrderDetailPath } from '../inventory/firm-order/inventory-firm-order-routing';
import { getJ5StateMetadataListItems } from '../j5/state/j5-state-list-items';
import { getPortfolioHoldDetailPath } from '../portfolio/hold/portfolio-hold-routing';
import { getPortfolioTaxLotAllocationListItems } from '../portfolio/transaction/portfolio-transaction-list-items';
import { getRegistrationAccountDetailPath } from '../registration/account/registration-account-routing';
import { getTrfReportDetailPath } from '../trf/report/trf-report-routing';
import { getProgramDetailPath } from '../userauth/program/program-routing';
import { getBatchOrderDetailPath } from './batch/batch-order-routing';
import { getOrderDetailPath } from './order-routing';
import { OrderSidePill } from './order-side-pill';
import { OrderStatusPill } from './order-status-pill';

export function getOrderTrfReportRefListItems(data: IxcOrdersV1TrfReportRef | undefined, prefix = '') {
  return {
    trfReportId: getReadonlyListItemId(`${prefix}trfReportId`, 'TRF Report ID', data?.trfReportId, getTrfReportDetailPath(data?.trfReportId)),
    executionId: getReadonlyListItemId(`${prefix}executionId`, 'Execution ID', data?.executionId),
    status: getReadonlyListItemTranslatedString(`${prefix}status`, 'TRF Status', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1TrfStatus',
      value: data?.status,
    }),
    errorMessage: data?.errorMessage ? getReadonlyListItemString(`${prefix}errorMessage`, 'Error Message', data.errorMessage) : undefined,
    updatedAt: getReadonlyListItemDate(`${prefix}updatedAt`, 'Updated At', data?.updatedAt, 'timestamp_iso_ms'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderControlsUsEquitySettingsListItems(data: IxcOrdersV1OrderControlsSettingsUsEquities | undefined, prefix = '') {
  return {
    canPlaceClosing: getReadonlyListItemYesOrNo(`${prefix}canPlaceClosing`, 'Place Closing Orders', Boolean(data?.canPlaceClosing)),
    canPlaceOpening: getReadonlyListItemYesOrNo(`${prefix}canPlaceOpening`, 'Place Opening Orders', Boolean(data?.canPlaceOpening)),
    canPlaceFractional: getReadonlyListItemYesOrNo(`${prefix}canPlaceFractional`, 'Trade Fractions', Boolean(data?.canTradeFractions)),
    canChargeCommission: getReadonlyListItemYesOrNo(`${prefix}canChargeCommission`, 'Charge Commission', Boolean(data?.canChargeCommission)),
    // notionalHoldBufferPercent: getReadonlyListItemNumber(
    //   `${prefix}notionalHoldBufferPercent`,
    //   'Notional Hold Buffer Percent',
    //   data?.notionalHoldBufferPercent,
    //   { formatStyle: 'percent' },
    // ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderControlsSettingsListItems(data: IxcOrdersV1OrderControlsSettings | undefined, prefix = '') {
  return {
    usEquities: getOrderControlsUsEquitySettingsListItems(data?.usEquities, `${prefix}usEquities.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderControlsListItems(data: IxcOrdersV1OrderControls | undefined, prefix = '') {
  return {
    settings: getOrderControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBatchOrderControlsUsEquitySettingsListItems(data: IxcOrdersV1BatchOrderControlsSettingsUsEquities | undefined, prefix = '') {
  return {
    canPlaceBatchOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceBatchOrders`, 'Place Batch Orders', Boolean(data?.canPlaceBatchOrders)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBatchOrderControlsSettingsListItems(data: IxcOrdersV1BatchOrderControlsSettings | undefined, prefix = '') {
  return {
    usEquities: getBatchOrderControlsUsEquitySettingsListItems(data?.usEquities, `${prefix}usEquities.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBatchOrderControlsListItems(data: IxcOrdersV1BatchOrderControls | undefined, prefix = '') {
  return {
    settings: getBatchOrderControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

function getTypeNameForOrderInstruction(data: IxcOrdersV1OrderInstruction | undefined) {
  if (!data) {
    return undefined;
  }

  if (data.limitPrice) {
    return 'Limit';
  }

  return 'Market';
}

export function getOrderTaxLotSelectionListItems(data: IxcOrdersV1OrderInstructionTaxLotSelection | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Tax Lot Selection', {
      namespace: 'ixcOrdersV1',
      oneOfName: 'IxcOrdersV1OrderInstructionTaxLotSelection',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if ('wellKnown' in data) {
    return {
      '!type': 'wellKnown',
      value: {
        ...typeNameColumn,
        method: getReadonlyListItemTranslatedString(`${prefix}wellKnown.method`, 'Method', {
          namespace: 'ixcPortfolioV1',
          enumName: 'IxcPortfolioV1TaxLotMethod',
          value: data.wellKnown?.method,
        }),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('custom' in data) {
    return {
      '!type': 'custom',
      value: {
        ...typeNameColumn,
        allocations: (data.custom?.allocations || []).map((allocation, i) =>
          getPortfolioTaxLotAllocationListItems(allocation, `${prefix}custom.allocations.${i}.`),
        ),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data);

  return undefined;
}

export function getOrderAmountListItems(data: IxcOrdersV1OrderAmount | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Amount Type', {
      namespace: 'ixcOrdersV1',
      oneOfName: 'IxcOrdersV1OrderAmount',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if ('quantity' in data) {
    return {
      '!type': 'quantity',
      value: {
        ...typeNameColumn,
        value: getReadonlyListItemNumber(`${prefix}quantity.value`, 'Quantity', data.quantity),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('notional' in data) {
    return {
      '!type': 'notional',
      value: {
        ...typeNameColumn,
        value: getReadonlyListItemCurrency(`${prefix}notional.value`, 'Notional Amount', data.notional),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data);

  return undefined;
}

export function getOrderInstructionListItems(data: IxcOrdersV1OrderInstruction | undefined, prefix = '') {
  return {
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    amount: getOrderAmountListItems(data?.amount, `${prefix}amount.`),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', data?.side ? <OrderSidePill side={data.side} /> : null),
    timeInForce: getReadonlyListItemTranslatedString(`${prefix}timeInForce`, 'Time in Force', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1OrderInstructionTimeInForce',
      value: data?.timeInForce,
    }),
    typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', getTypeNameForOrderInstruction(data)),
    limitPrice: data?.limitPrice ? getReadonlyListItemCurrency(`${prefix}limitPrice`, 'Limit Price', data?.limitPrice) : undefined,
    // Tax lot selection is only applicable to sell orders
    taxLotSelection:
      data?.side === IxcOrdersV1Side.Sell ? getOrderTaxLotSelectionListItems(data.taxLotSelection, `${prefix}taxLotSelection.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderCommissionListItems(data: IxcOrdersV1Commission | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Commission', {
      namespace: 'ixcOrdersV1',
      oneOfName: 'IxcOrdersV1Commission',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if ('fixed' in data) {
    return {
      '!type': 'fixed',
      value: {
        ...typeNameColumn,
        amount: getReadonlyListItemCurrency(`${prefix}fixed.amount`, 'Fixed Commission Amount', data.fixed?.amount),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('simpleRate' in data) {
    return {
      '!type': 'simpleRate',
      value: {
        ...typeNameColumn,
        rate: getReadonlyListItemNumber(`${prefix}simpleRate.rate`, 'Commission Rate', data.simpleRate?.rate),
        basis: getReadonlyListItemTranslatedString(`${prefix}simpleRate.basis`, 'Commission Basis', {
          namespace: 'ixcOrdersV1',
          enumName: 'IxcOrdersV1CommissionSimpleRateBasis',
          value: data.simpleRate?.basis,
        }),
        minimum: data.simpleRate?.minimum
          ? getReadonlyListItemCurrency(`${prefix}simpleRate.minimum`, 'Minimum Commission', data.simpleRate?.minimum)
          : undefined,
        maximum: data.simpleRate?.maximum
          ? getReadonlyListItemCurrency(`${prefix}simpleRate.maximum`, 'Maximum Commission', data.simpleRate?.maximum)
          : undefined,
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data);

  return undefined;
}

export function getOrderExtensionListItems(data: IxcOrdersV1OrderExtension | undefined, prefix = '') {
  return {
    trailer: getReadonlyListItemString(`${prefix}trailer`, 'Trailer', data?.trailer),
    solicited: getReadonlyListItemYesOrNo(`${prefix}solicited`, 'Solicited', getBooleanFromUndefinedOrNullable(data, 'solicited')),
    commission: data?.commission ? getOrderCommissionListItems(data?.commission, `${prefix}commission`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderAmountConversionListItems(data: IxcOrdersV1OrderAmountConversion | undefined, prefix = '') {
  return {
    amount: getOrderAmountListItems(data?.amount, `${prefix}amount.`),
    reason: getReadonlyListItemTranslatedString(`${prefix}reason`, 'Reason', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1OrderAmountConversionReason',
      value: data?.reason,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderDataListItems(
  data: IxcOrdersV1OrderData | undefined,
  accountId: string | undefined,
  viewContext: ViewContextType,
  prefix = '',
) {
  return {
    instruction: getOrderInstructionListItems(data?.instruction, `${prefix}instruction`),
    extension: data?.extension ? getOrderExtensionListItems(data?.extension, `${prefix}extension`) : undefined,
    fixOrderId:
      viewContext === 'rollup-clearing' && data?.fixOrderId
        ? getReadonlyListItemId(`${prefix}fixOrderId`, 'FIX Order ID', data?.fixOrderId, getFixOrderDetailPath(data.fixOrderId))
        : undefined,
    booksOrderId:
      viewContext === 'rollup-clearing' && data?.booksOrderId
        ? getReadonlyListItemId(`${prefix}booksOrderId`, 'Books Order ID', data?.booksOrderId, getInventoryBooksOrderDetailPath(data.booksOrderId))
        : undefined,
    holdId: data?.holdId
      ? getReadonlyListItemId(`${prefix}holdId`, 'Hold ID', data.holdId, getPortfolioHoldDetailPath(accountId, data.holdId))
      : undefined,
    batchOrderId: data?.batchOrderId
      ? getReadonlyListItemId(`${prefix}batchOrderId`, 'Batch Order ID', data.batchOrderId, getBatchOrderDetailPath(data.batchOrderId))
      : undefined,
    firmOrderId: data?.firmOrderId
      ? getReadonlyListItemString(`${prefix}firmOrderId`, 'Firm Order ID', data.firmOrderId, getInventoryFirmOrderDetailPath(data.firmOrderId))
      : undefined,
    asset: data?.asset ? getAssetStateListItems(data.asset) : undefined,
    quotePrice: data?.quotePrice ? getReadonlyListItemCurrency(`${prefix}quotePrice`, 'Quote Price', data.quotePrice) : undefined,
    averagePrice: data?.averagePrice ? getReadonlyListItemCurrency(`${prefix}averagePrice`, 'Average Price', data.averagePrice) : undefined,
    totalAmount: data?.totalAmount ? getReadonlyListItemCurrency(`${prefix}totalAmount`, 'Total Amount', data.totalAmount) : undefined,
    totalQuantity: data?.totalQuantity ? getReadonlyListItemNumber(`${prefix}totalQuantity`, 'Total Quantity', data.totalQuantity) : undefined,
    tradingHours: getReadonlyListItemTranslatedString('tradingHours', 'Trading Hours', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1TradingHours',
      value: data?.tradingHours,
    }),
    amountConversion: data?.amountConversion ? getOrderAmountConversionListItems(data.amountConversion, `${prefix}amountConversion.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderStateListItems(
  data: IxcOrdersV1OrderState | undefined,
  viewContext: ViewContextType,
  linkContext: ReplaceValuesAndMakeOptional<IxcOrdersV1OrderKeys, string, boolean> = { accountId: true, programId: true },
) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    orderId: getReadonlyListItemId('orderId', 'Order ID', data?.orderId, linkContext.orderId ? getOrderDetailPath(data?.orderId) : undefined),
    accountId: getReadonlyListItemId(
      'accountId',
      'Account ID',
      data?.accountId,
      linkContext.accountId ? getRegistrationAccountDetailPath(data?.accountId) : undefined,
    ),
    programId: getReadonlyListItemId(
      'programId',
      'Program ID',
      data?.programId,
      linkContext.programId ? getProgramDetailPath(data?.programId) : undefined,
    ),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1OrderStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <OrderStatusPill status={data.status} /> : null),
    data: getOrderDataListItems(data?.data, data?.accountId, viewContext, 'data'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderMiddleMoneyListItems(data: IxcOrdersV1MiddleMoney | undefined, prefix = '') {
  return {
    class: getReadonlyListItemTranslatedString(`${prefix}class`, 'Class', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1MiddleMoneyClass',
      value: data?.class,
    }),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderMiddleMoneyListItemsWithSummary(data: IxcOrdersV1MiddleMoney[] | undefined, prefix = '') {
  return {
    middleMoney: (data || []).map((middleMoney, i) => getOrderMiddleMoneyListItems(middleMoney, `${prefix}middleMoney.${i}`)),
    middleMoneyAsKeyValuePairs: (data || []).map((middleMoney, i) =>
      getReadonlyListItemCurrency(
        `${prefix}middleMoney.${i}`,
        <TranslatedText
          defaultValue={middleMoney.class}
          i18nKey={{ namespace: 'ixcOrdersV1', enumName: 'IxcOrdersV1MiddleMoneyClass', value: middleMoney.class }}
        />,
        middleMoney.amount,
      ),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderTradeTransactionListItems(data: IxcOrdersV1TradeTransaction | undefined, prefix = '') {
  return {
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    executionId: getReadonlyListItemId(`${prefix}executionId`, 'Execution ID', data?.executionId),
    executionTime: getReadonlyListItemDate(`${prefix}executionTime`, 'Execution Time', data?.executionTime, 'timestamp_iso_ms'),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    counterparty: getReadonlyListItemTranslatedString(`${prefix}counterparty`, 'Counterparty', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Counterparty',
      value: data?.counterparty,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', data?.side ? <OrderSidePill side={data.side} /> : null),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    price: getReadonlyListItemCurrency(`${prefix}price`, 'Price', data?.price),
    grossAmount: getReadonlyListItemCurrency(`${prefix}grossAmount`, 'Gross Amount', data?.grossAmount),
    netAmount: getReadonlyListItemCurrency(`${prefix}netAmount`, 'Net Amount', data?.netAmount),
    solicited: getReadonlyListItemYesOrNo(`${prefix}solicited`, 'Solicited', getBooleanFromUndefinedOrNullable(data, 'solicited')),
    trailer: getReadonlyListItemString(`${prefix}trailer`, 'Trailer', data?.trailer),
    capacity: getReadonlyListItemTranslatedString(`${prefix}capacity`, 'Capacity', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Capacity',
      value: data?.capacity,
    }),
    market: getReadonlyListItemTranslatedString(`${prefix}market`, 'Market', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Market',
      value: data?.market,
    }),
    priceSource: getReadonlyListItemTranslatedString(`${prefix}priceSource`, 'Price Source', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1PriceSource',
      value: data?.priceSource,
    }),
    // Preferably show a table for middle money or use the key-value pair list
    ...getOrderMiddleMoneyListItemsWithSummary(data?.middleMoney, `${prefix}middleMoney.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrderDetailPageListItems(data: IxcOrdersV1OrderState | undefined, viewContext: ViewContext) {
  const listItems = getOrderStateListItems(data, viewContext.kind);

  const leftColumn: ReadonlyListGroup[] = [
    {
      id: 'main',
      items: [
        listItems.data.asset?.summary || listItems.data.instruction.symbol,
        match(listItems.data.instruction.amount)
          .with({ '!type': 'quantity' }, (a) => a.value.value)
          .with({ '!type': 'notional' }, (a) => a.value.value)
          .otherwise(() => getReadonlyListItemString('amount', !data ? <Skeleton charCount={6} type="line" /> : 'Amount', undefined)),
        ...(listItems.data.amountConversion
          ? [
              match(listItems.data.amountConversion.amount)
                .with({ '!type': 'quantity' }, (a) => ({ ...a.value.value, title: 'Converted Quantity' }))
                .with({ '!type': 'notional' }, (a) => ({ ...a.value.value, title: 'Converted Notional Value' }))
                .otherwise(() => getReadonlyListItemString('amount', !data ? <Skeleton charCount={6} type="line" /> : 'Converted Amount', undefined)),
              { ...listItems.data.amountConversion.reason, title: 'Amount Conversion Reason' },
            ]
          : []),
        listItems.data.instruction.typeName,
        ...(listItems.data.instruction.limitPrice ? [listItems.data.instruction.limitPrice] : []),
        listItems.data.instruction.timeInForce,
        ...(listItems.data.quotePrice ? [listItems.data.quotePrice] : []),
        ...(listItems.data.averagePrice ? [listItems.data.averagePrice] : []),
        ...(listItems.data.totalQuantity ? [listItems.data.totalQuantity] : []),
        ...(listItems.data.totalAmount ? [listItems.data.totalAmount] : []),
        listItems.data.tradingHours,
      ],
    },
  ];

  if (listItems.data.extension) {
    leftColumn.push({
      id: 'extension',
      items: [
        listItems.data.extension.solicited,
        listItems.data.extension.trailer,
        ...(listItems.data.extension.commission
          ? [
              listItems.data.extension.commission.value.typeName,
              ...match(listItems.data.extension.commission)
                .with({ '!type': 'fixed' }, (c) => [c.value.amount])
                .with({ '!type': 'simpleRate' }, (c) => {
                  const base = [c.value.rate, c.value.basis];

                  if (c.value.minimum) {
                    base.push(c.value.minimum);
                  }

                  if (c.value.maximum) {
                    base.push(c.value.maximum);
                  }

                  return base;
                })
                .otherwise(() => []),
            ]
          : [getReadonlyListItemString('commission', 'Commission', undefined)]),
      ],
    });
  }

  return {
    left: leftColumn,
    right: [
      listItems.metadata.createdAt,
      listItems.metadata.updatedAt,
      listItems.orderId,
      listItems.accountId,
      listItems.programId,
      ...(listItems.data.holdId ? [listItems.data.holdId] : []),
      ...(listItems.data.booksOrderId ? [listItems.data.booksOrderId] : []),
      ...(listItems.data.fixOrderId ? [listItems.data.fixOrderId] : []),
      ...(listItems.data.batchOrderId ? [listItems.data.batchOrderId] : []),
      ...(listItems.data.firmOrderId ? [listItems.data.firmOrderId] : []),
    ] as const satisfies ReadonlyListItem[],
  };
}

export function getOrdersBatchAllocationAveragePriceTransactionListItems(
  data: IxcOrdersV1BatchAllocationAvgPriceTransaction | undefined,
  prefix = '',
) {
  return {
    batchOrderId: getReadonlyListItemId(`${prefix}batchOrderId`, 'Batch Order ID', data?.batchOrderId, getBatchOrderDetailPath(data?.batchOrderId)),
    batchAllocationId: getReadonlyListItemId(`${prefix}batchAllocationId`, 'Batch Allocation ID', data?.batchAllocationId),
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', data?.side ? <OrderSidePill side={data.side} /> : undefined),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    market: getReadonlyListItemTranslatedString(`${prefix}market`, 'Market', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Market',
      value: data?.market,
    }),
    capacity: getReadonlyListItemTranslatedString(`${prefix}capacity`, 'Capacity', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Capacity',
      value: data?.capacity,
    }),
    ...getOrderMiddleMoneyListItemsWithSummary(data?.middleMoney, `${prefix}middleMoney.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrdersBatchAllocationCustomerTransactionListItems(data: IxcOrdersV1BatchAllocationCustomerTransaction | undefined, prefix = '') {
  return {
    batchOrderId: getReadonlyListItemId(`${prefix}batchOrderId`, 'Batch Order ID', data?.batchOrderId, getBatchOrderDetailPath(data?.batchOrderId)),
    batchAllocationId: getReadonlyListItemId(`${prefix}batchAllocationId`, 'Batch Allocation ID', data?.batchAllocationId),
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', <OrderSidePill side={data?.side} />),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    market: getReadonlyListItemTranslatedString(`${prefix}market`, 'Market', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Market',
      value: data?.market,
    }),
    capacity: getReadonlyListItemTranslatedString(`${prefix}capacity`, 'Capacity', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Capacity',
      value: data?.capacity,
    }),
    ...getOrderMiddleMoneyListItemsWithSummary(data?.middleMoney, `${prefix}middleMoney.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getOrdersBatchRemainderInventoryTransactionListItems(data: IxcOrdersV1BatchRemainderInventoryTransaction | undefined, prefix = '') {
  return {
    batchOrderId: getReadonlyListItemId(`${prefix}batchOrderId`, 'Batch Order ID', data?.batchOrderId, getBatchOrderDetailPath(data?.batchOrderId)),
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', <OrderSidePill side={data?.side} />),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    market: getReadonlyListItemTranslatedString(`${prefix}market`, 'Market', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Market',
      value: data?.market,
    }),
    capacity: getReadonlyListItemTranslatedString(`${prefix}capacity`, 'Capacity', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Capacity',
      value: data?.capacity,
    }),
    ...getOrderMiddleMoneyListItemsWithSummary(data?.middleMoney, `${prefix}middleMoney.`),
  } as const satisfies ReadonlyListItemRecord;
}
